/**
 * Same-origin notification sound. Remote Netlify URLs fail after long idle with
 * Chrome `ERR_CACHE_OPERATION_NOT_SUPPORTED` on the media element cache.
 */
export const NOTIFICATION_SOUND_URL = "/sound/notification.mp3";

export function createNotificationAudio() {
  const audio = new Audio(NOTIFICATION_SOUND_URL);
  audio.preload = "auto";
  return audio;
}

/**
 * Play from a React ref. On failure (stale/cached element after idle), recreate
 * the Audio instance and retry once.
 *
 * @param {{ current: HTMLAudioElement | null }} audioRef
 * @returns {Promise<void> | void}
 */
export function playNotificationAudio(audioRef) {
  if (!audioRef) return;

  const ensure = () => {
    if (!audioRef.current) {
      audioRef.current = createNotificationAudio();
    }
    return audioRef.current;
  };

  const tryPlay = (el) => {
    try {
      el.pause();
    } catch (_) {}
    try {
      el.currentTime = 0;
    } catch (_) {}
    return el.play();
  };

  const audio = ensure();
  let p;
  try {
    p = tryPlay(audio);
  } catch (err) {
    console.warn("Notification sound error:", err);
    return;
  }

  if (!p || typeof p.then !== "function") return p;

  return p.catch(() => {
    const fresh = createNotificationAudio();
    audioRef.current = fresh;
    return tryPlay(fresh).catch((err) => {
      console.warn("Notification sound blocked/failed:", err);
    });
  });
}

/**
 * Unlock autoplay with a muted play during a user gesture.
 *
 * @param {HTMLAudioElement | null | undefined} audio
 */
export function unlockNotificationAudio(audio) {
  if (!audio) return;
  try {
    audio.muted = true;
    const p = audio.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      }).catch(() => {
        audio.muted = false;
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
    }
  } catch (_) {}
}
